import React, { useEffect } from 'react';
import PropTypes from 'prop-types';


// Small replacement for react-helmet: only <title> was used in the app.
// React 19 hoists every rendered <title> to <head> and the browser then uses
// the first one, so nested titles (app default + page) would not work.
// This keeps react-helmet semantics: the most recently mounted title wins,
// a component marked as `defaultTitle` is used only when no page sets its own
// title, and unmounting restores the previous title.

let entries = [];
let defaultEntry = null;
let nextId = 0;

const applyTitle = () => {
  const entry = entries.length ? entries[entries.length - 1] : defaultEntry;
  if (entry && typeof document !== 'undefined') {
    document.title = entry.title;
  }
};

const getText = (node) => {
  if (node === null || node === undefined || typeof node === 'boolean') {
    return '';
  }
  if (typeof node === 'string' || typeof node === 'number') {
    return String(node);
  }
  if (Array.isArray(node)) {
    return node.map(getText).join('');
  }
  if (React.isValidElement(node)) {
    return getText(node.props.children);
  }
  return '';
};

const extractTitle = (children) => {
  let title = null;
  React.Children.forEach(children, (child) => {
    if (React.isValidElement(child) && child.type === 'title') {
      title = getText(child.props.children);
    }
  });
  return title;
};

export const Helmet = ({ children = null, defaultTitle = false }) => {
  const title = extractTitle(children);

  useEffect(() => {
    if (title === null) {
      return undefined;
    }
    nextId += 1;
    const entry = { id: nextId, title };
    if (defaultTitle) {
      defaultEntry = entry;
    } else {
      entries = [...entries, entry];
    }
    applyTitle();
    return () => {
      if (defaultTitle) {
        if (defaultEntry === entry) {
          defaultEntry = null;
        }
      } else {
        entries = entries.filter(item => item !== entry);
      }
      applyTitle();
    };
  }, [title, defaultTitle]);

  return null;
};

Helmet.propTypes = {
  children: PropTypes.node,
  defaultTitle: PropTypes.bool,
};

export default Helmet;
